import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Button, Card, CardContent, Grid, TextField,
  Autocomplete, IconButton, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, Paper, CssBaseline, Divider, InputAdornment,
} from '@mui/material';
import { ArrowBack, Add, DeleteOutline, Save, LocalShippingOutlined, FingerprintOutlined } from '@mui/icons-material';
import AppTheme from '../../../shared-theme/AppTheme';
import SideMenu from '../../../components/SideMenu';
import Header from '../../../components/Header';
import { StyledCellHeader } from '../../../components/StyledCellHeader';
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';
import { fetchAlmacenes } from '../../../utils/generalsFetch';
import { useNavigate } from 'react-router-dom';
import { resumenCampos, esSerializado, nombreBusqueda } from './utils';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Usuario-ID': localStorage.getItem('id'),
  'X-company-ID': localStorage.getItem('idcompany'),
});

export default function CrearTransferencia() {
  const navigate = useNavigate();
  const [almacenes, setAlmacenes] = useState([]);
  const [origen, setOrigen] = useState(null);
  const [destino, setDestino] = useState(null);
  const [transportista, setTransportista] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [productos, setProductos] = useState([]);
  const [items, setItems] = useState([]); // { id_producto_origen, cantidad_enviada, producto }
  const [productoSeleccion, setProductoSeleccion] = useState(null);
  const [cantidadDraft, setCantidadDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendAfter, setSendAfter] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'error' });

  useEffect(() => {
    fetchAlmacenes({ idcompany: localStorage.getItem('idcompany'), usuarioId: localStorage.getItem('id') })
      .then(d => {
        // El endpoint devuelve { value, label, direccion } — normalizo a { id, nombre } para el UI.
        const normalizados = Array.isArray(d)
          ? d.map(a => ({ id: a.value || a.id, nombre: a.label || a.nombre, direccion: a.direccion }))
          : [];
        setAlmacenes(normalizados);
      })
      .catch(() => setAlmacenes([]));
  }, []);

  const fetchProductosAlmacen = useCallback(async (idalmacen) => {
    if (!idalmacen) { setProductos([]); return; }
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/transferencias_mercancia/productos_disponibles/${idalmacen}`, { headers: authHeaders() });
      const data = await res.json();
      setProductos(Array.isArray(data) ? data : []);
    } catch {
      setProductos([]);
    }
  }, []);

  useEffect(() => {
    fetchProductosAlmacen(origen?.id);
    setItems([]);
    setProductoSeleccion(null);
    setCantidadDraft('');
  }, [origen, fetchProductosAlmacen]);

  const productosNoAgregados = useMemo(
    () => productos.filter(p => !items.some(i => i.id_producto_origen === p.id)),
    [productos, items],
  );

  const handleAddItem = () => {
    if (!productoSeleccion) { setAlert({ open: true, message: 'Seleccione un producto', severity: 'error' }); return; }
    const cantidad = parseInt(cantidadDraft, 10);
    if (!cantidad || cantidad <= 0) { setAlert({ open: true, message: 'Cantidad inválida', severity: 'error' }); return; }
    if (cantidad > productoSeleccion.cantidad) { setAlert({ open: true, message: `Solo hay ${productoSeleccion.cantidad} disponibles`, severity: 'error' }); return; }
    if (esSerializado(productoSeleccion) && cantidad !== productoSeleccion.cantidad) {
      setAlert({ open: true, message: 'Los productos serializados deben transferirse como unidad completa', severity: 'error' });
      return;
    }
    setItems([...items, { id_producto_origen: productoSeleccion.id, cantidad_enviada: cantidad, producto: productoSeleccion }]);
    setProductoSeleccion(null);
    setCantidadDraft('');
  };

  // Auto-prellenar cantidad cuando se selecciona un serializado (no es editable).
  useEffect(() => {
    if (productoSeleccion && esSerializado(productoSeleccion)) {
      setCantidadDraft(String(productoSeleccion.cantidad));
    }
  }, [productoSeleccion]);

  const handleRemoveItem = (id) => setItems(items.filter(i => i.id_producto_origen !== id));

  const handleSave = async (alsoSend = false) => {
    if (!origen || !destino) { setAlert({ open: true, message: 'Seleccione origen y destino', severity: 'error' }); return; }
    if (origen.id === destino.id) { setAlert({ open: true, message: 'Origen y destino deben ser distintos', severity: 'error' }); return; }
    if (items.length === 0) { setAlert({ open: true, message: 'Agregue al menos un producto', severity: 'error' }); return; }

    setSaving(true);
    try {
      const body = {
        idcompany: localStorage.getItem('idcompany'),
        id_almacen_origen: origen.id,
        id_almacen_destino: destino.id,
        transportista: transportista.trim(),
        observaciones_envio: observaciones.trim(),
        items: items.map(i => ({ id_producto_origen: i.id_producto_origen, cantidad_enviada: i.cantidad_enviada })),
        created_by: localStorage.getItem('id'),
      };
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/transferencias_mercancia`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Error al crear transferencia');

      if (alsoSend) {
        const sendRes = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/transferencias_mercancia/${result.id}/enviar`, {
          method: 'POST', headers: authHeaders(), body: JSON.stringify({ enviado_por: localStorage.getItem('id') }),
        });
        if (!sendRes.ok) {
          const err = await sendRes.json();
          throw new Error(err.error || 'Transferencia creada pero fallo el envío');
        }
      }
      navigate(`/logistica/transferencias/detalle/${result.id}`);
    } catch (err) {
      setAlert({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const totalItems = items.reduce((acc, i) => acc + i.cantidad_enviada, 0);

  return (
    <Box>
      <AppTheme themeComponents={{}}>
        <CssBaseline enableColorScheme />
        <Box sx={{ display: 'flex' }}>
          <SideMenu />
          <Box component="main" sx={{ flexGrow: 1, overflow: 'auto', bgcolor: 'background.default', minHeight: '100vh' }}>
            <Stack sx={{ alignItems: 'stretch', pb: 6, mt: { xs: 8, md: 0 } }}>
              <Header parent="Logística" child="Nueva Transferencia" />

              <Box sx={{ px: { xs: 2, md: 4 }, pt: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Button variant="text" size="small" startIcon={<ArrowBack />} onClick={() => navigate('/logistica/transferencias')} sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'none' }}>
                    Transferencias
                  </Button>
                  <Divider orientation="vertical" flexItem />
                  <Box>
                    <Typography variant="h5" fontWeight={800} letterSpacing="-0.5px">Nueva transferencia</Typography>
                    <Typography variant="body2" color="text.secondary">Envío de mercancía entre almacenes</Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item size={{ xs: 12, md: 5 }}>
                    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 2 }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Origen y destino</Typography>
                        <Stack spacing={2}>
                          <Autocomplete
                            options={almacenes} value={origen}
                            onChange={(_, v) => setOrigen(v)}
                            getOptionLabel={(o) => o?.nombre || ''}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderInput={(params) => <TextField {...params} size="small" label="Almacén origen" />}
                          />
                          <Autocomplete
                            options={almacenes.filter(a => a.id !== origen?.id)} value={destino}
                            onChange={(_, v) => setDestino(v)}
                            getOptionLabel={(o) => o?.nombre || ''}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderInput={(params) => <TextField {...params} size="small" label="Almacén destino" />}
                          />
                          <TextField
                            size="small" fullWidth
                            label="Transportista"
                            placeholder="Nombre o empresa de transporte"
                            value={transportista} onChange={(e) => setTransportista(e.target.value)}
                          />
                          <TextField
                            size="small" fullWidth multiline minRows={3}
                            label="Observaciones de envío"
                            value={observaciones} onChange={(e) => setObservaciones(e.target.value)}
                          />
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Agregar producto</Typography>
                        <Stack spacing={1.5}>
                          <Autocomplete
                            options={productosNoAgregados} value={productoSeleccion}
                            onChange={(_, v) => { setProductoSeleccion(v); setCantidadDraft(''); }}
                            disabled={!origen}
                            getOptionLabel={(o) => o ? nombreBusqueda(o) : ''}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderOption={(props, option) => {
                              const serial = esSerializado(option);
                              const primary = nombreBusqueda(option);
                              const secondary = serial ? option.nombre : null;
                              const campos = resumenCampos(option, 3, serial ? ['id'] : []);
                              return (
                                <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important' }}>
                                  <Stack direction="row" alignItems="center" spacing={0.75}>
                                    {serial && <FingerprintOutlined sx={{ fontSize: 14, color: 'primary.main' }} />}
                                    <Typography variant="body2" fontWeight={600}>{primary}</Typography>
                                    <Typography variant="caption" color="text.secondary">· stock {option.cantidad}</Typography>
                                  </Stack>
                                  {secondary && <Typography variant="caption" color="text.secondary">{secondary}</Typography>}
                                  {campos && <Typography variant="caption" color="text.secondary">{campos}</Typography>}
                                </Box>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField {...params} size="small" label="Producto" placeholder={origen ? 'Buscar por nombre o identificador' : 'Primero seleccione origen'} />
                            )}
                          />
                          {productoSeleccion && (
                            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.default' }}>
                              <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
                                {esSerializado(productoSeleccion) && <FingerprintOutlined sx={{ fontSize: 14, color: 'primary.main' }} />}
                                <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem' }}>
                                  {esSerializado(productoSeleccion) ? nombreBusqueda(productoSeleccion) : productoSeleccion.categoria_nombre || 'Producto'}
                                </Typography>
                              </Stack>
                              {esSerializado(productoSeleccion) && (
                                <Typography variant="caption" color="text.secondary" display="block">{productoSeleccion.nombre}</Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" display="block">
                                {resumenCampos(productoSeleccion, 3, esSerializado(productoSeleccion) ? ['id'] : []) || 'Sin campos personalizados'}
                              </Typography>
                            </Box>
                          )}
                          <Stack direction="row" spacing={1}>
                            <TextField
                              size="small" fullWidth type="number" label="Cantidad"
                              value={cantidadDraft}
                              onChange={(e) => setCantidadDraft(e.target.value)}
                              disabled={!productoSeleccion || esSerializado(productoSeleccion)}
                              helperText={productoSeleccion && esSerializado(productoSeleccion) ? 'Unidad serializada: cantidad fija' : ''}
                              slotProps={{
                                input: { endAdornment: productoSeleccion && <InputAdornment position="end">/ {productoSeleccion.cantidad}</InputAdornment> },
                              }}
                            />
                            <Button variant="contained" color="primarybutton" startIcon={<Add />} onClick={handleAddItem} disabled={!productoSeleccion || !cantidadDraft} sx={{ fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap', height: 40 }}>
                              Agregar
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item size={{ xs: 12, md: 7 }}>
                    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="subtitle2" fontWeight={700}>Items a transferir ({items.length})</Typography>
                          <Typography variant="caption" color="text.secondary">Total unidades: <strong>{totalItems}</strong></Typography>
                        </Stack>

                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <StyledCellHeader>Producto</StyledCellHeader>
                                <StyledCellHeader>Categoría</StyledCellHeader>
                                <StyledCellHeader sx={{ textAlign: 'center' }}>Cantidad</StyledCellHeader>
                                <StyledCellHeader sx={{ width: 50 }} />
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {items.length === 0 && (
                                <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>Aún no hay items. Agrega productos desde el panel de la izquierda.</TableCell></TableRow>
                              )}
                              {items.map((i) => {
                                const serial = esSerializado(i.producto);
                                const primary = nombreBusqueda(i.producto);
                                const campos = resumenCampos(i.producto, 3, serial ? ['id'] : []);
                                return (
                                  <TableRow key={i.id_producto_origen} hover>
                                    <TableCell>
                                      <Stack direction="row" alignItems="center" spacing={0.5}>
                                        {serial && <FingerprintOutlined sx={{ fontSize: 14, color: 'primary.main' }} />}
                                        <Typography variant="body2" fontWeight={600}>{primary}</Typography>
                                      </Stack>
                                      {serial && <Typography variant="caption" color="text.secondary" display="block">{i.producto.nombre}</Typography>}
                                      {campos && <Typography variant="caption" color="text.secondary" display="block">{campos}</Typography>}
                                    </TableCell>
                                    <TableCell><Typography variant="caption" color="text.secondary">{i.producto.categoria_nombre || '—'}</Typography></TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>{i.cantidad_enviada}</TableCell>
                                    <TableCell>
                                      <IconButton size="small" onClick={() => handleRemoveItem(i.id_producto_origen)}>
                                        <DeleteOutline sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Stack direction="row" justifyContent="flex-end" spacing={1} mt={3}>
                          <Button variant="outlined" onClick={() => navigate('/logistica/transferencias')} disabled={saving} sx={{ fontWeight: 600, textTransform: 'none' }}>
                            Cancelar
                          </Button>
                          <Button variant="outlined" color="primarybutton" startIcon={<Save />} onClick={() => handleSave(false)} disabled={saving} sx={{ fontWeight: 700, textTransform: 'none' }}>
                            Guardar pendiente
                          </Button>
                          <Button variant="contained" color="primarybutton" startIcon={<LocalShippingOutlined />} onClick={() => handleSave(true)} disabled={saving} sx={{ fontWeight: 700, textTransform: 'none' }}>
                            Guardar y enviar
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </Box>
        </Box>

        <AlertMessageDialog open={alert.open} handleClose={() => setAlert({ ...alert, open: false })} title="" message={alert.message} severity={alert.severity} color={alert.severity} />
      </AppTheme>
    </Box>
  );
}
